/**
 * Hyperlight jump effects. The charge-phase streaks are the real
 * background stars (see StarSystem.setStarStreak); this module provides the
 * tunnel the ship rides through during the jump itself.
 */
import * as THREE from "three";

/**
 * Star-Wars-style hyperspace tunnel: a swirling cylinder of light AROUND the
 * ship. The chase camera (inside the tube with the ship) keeps its normal
 * follow behavior; the walls scroll past and slowly rotate for the swirl.
 */
export class WarpTunnel {
  private mesh: THREE.Mesh;
  private mat: THREE.MeshBasicMaterial;
  private tex: THREE.CanvasTexture;
  private spin = 0;
  private spinQ = new THREE.Quaternion();
  private axisZ = new THREE.Vector3(0, 0, 1);

  constructor(scene: THREE.Scene) {
    this.tex = makeTunnelTexture();
    this.tex.wrapS = THREE.RepeatWrapping;
    this.tex.wrapT = THREE.RepeatWrapping;
    // ONE repeat along the tube: repeating the texture lengthwise made the
    // walls read as discrete "rings" flying past instead of a continuous
    // rotating tunnel (the streaks wrap seamlessly, see the texture fn).
    this.tex.repeat.set(4, 1);
    this.mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide, // we're inside the tube
      depthWrite: false,
      fog: false,
    });
    const geo = new THREE.CylinderGeometry(150, 150, 2600, 40, 1, true);
    geo.rotateX(Math.PI / 2); // axis along Z (ship forward)
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  /** intensity 0..1; the tube wraps the ship and follows its frame. */
  update(
    intensity: number,
    dt: number,
    shipRenderPos: THREE.Vector3,
    quat: THREE.Quaternion,
    flow = 1,
  ): void {
    if (intensity <= 0.01) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    this.mat.opacity = intensity * 0.6;
    this.mesh.position.copy(shipRenderPos);
    // The tunnel ROTATES around the player — the dominant motion cue —
    // while the walls stream past underneath it.
    this.spin += dt * 0.75;
    this.spinQ.setFromAxisAngle(this.axisZ, this.spin);
    this.mesh.quaternion.copy(quat).multiply(this.spinQ);
    // Walls race past at the ride's own pace, swirl drifts sideways.
    this.tex.offset.y -= dt * (0.6 + flow * intensity * 2.2);
    this.tex.offset.x += dt * 0.07;
  }
}

/**
 * Streaky light texture for the tunnel walls (v = along the tube). Streaks
 * are LONG and drawn twice (y and y − h) so the texture wraps seamlessly
 * along the tube — short mid-faded dashes tiled twice were what read as
 * "rings" marching at the player.
 */
function makeTunnelTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 512;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const drawStreak = (x: number, y: number, len: number, lw: number, b: number, a: number) => {
    const grad = ctx.createLinearGradient(x, y, x, y + len);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(0.3, `rgba(${b * 0.6},${b * 0.85},${b},${a})`);
    grad.addColorStop(0.7, `rgba(${b * 0.6},${b * 0.85},${b},${a})`);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.strokeStyle = grad;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + len);
    ctx.stroke();
  };
  for (let i = 0; i < 110; i++) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const len = 220 + Math.random() * 480;
    const lw = 1 + Math.random() * 2.5;
    const b = 150 + Math.floor(Math.random() * 105);
    const a = 0.1 + Math.random() * 0.26;
    drawStreak(x, y, len, lw, b, a);
    drawStreak(x, y - h, len, lw, b, a); // wrap copy: seamless along the tube
  }
  return new THREE.CanvasTexture(canvas);
}
